'use client';

import { useRouter, usePathname } from 'next/navigation';
import { Home, Search, Users, Building2, Megaphone, Inbox, CheckSquare, BarChart3, Settings, Bell, Plus, TrendingUp, Phone, PhoneCall, Zap, User as UserIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserHierarchy } from '@/lib/query/hooks/admin';
import { User } from '@/lib/api/types';
import { useUserSelection } from '@/components/context/UserSelectionContext';

export default function TopBar() {
    const navigate = useRouter();
    const pathname = usePathname();
    const { data, isLoading, error } = useUserHierarchy();
    const { selectedUser, setSelectedUser } = useUserSelection();


    const users = data?.data || [];

    const handleUserChange = (userId: string) => {
        if (userId === "all") {
            setSelectedUser(undefined);
            return;
        }
        const user = users.find((u) => u.user_id === userId);
        if (user) {
            const selectedUserObj: User = {
                id: user.user_id,
                name: user.name,
                email: "", // HierarchyUser doesn't have email, but User interface requires it.
                role: user.role,
            };
            setSelectedUser(selectedUserObj);
        }
    };

    return (
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
            <div className="flex-1 max-w-xl">
            </div>

            <div className="flex items-center gap-3">
                {/* User Selection Dropdown - Only visible on home page */}

                {users.length > 0 && (
                    <div className="w-64">
                        <Select
                            value={selectedUser?.id || "all"}
                            onValueChange={handleUserChange}
                            disabled={isLoading}
                        >
                            <SelectTrigger data-testid="user-select-trigger">
                                {/* <User className="w-4 h-4 text-gray-500" /> */}
                                <SelectValue placeholder={isLoading ? "Loading users..." : "Select a user"} />

                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">My Data</SelectItem>
                                {error ? (
                                    <div className="px-2 py-1.5 text-sm text-red-500">
                                        Failed to load users
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="px-2 py-1.5 text-sm text-gray-500">
                                        No users found
                                    </div>
                                ) : (
                                    users?.map((user) => (
                                        <SelectItem
                                            key={user.user_id}
                                            value={user.user_id}
                                            data-testid={`user-option-${user.user_id}`}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">{user.name}</span>
                                                {user.role && (
                                                    <span className="text-xs text-gray-500">{user.role}</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                )}


                <Button
                    onClick={() => navigate.push('/campaigns/new')}
                    data-testid="add-button"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    New Campaign
                </Button>
                {/* <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="notifications-button">
                    <Bell className="w-5 h-5 text-gray-600" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button> */}
            </div>
        </div>
    );
}